/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment, type PointerEvent, useCallback, useRef, useState } from 'react';

import { motion, type Transition, useReducedMotion } from 'motion/react';
import { FormattedMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import LinkButton from '@atlaskit/button/link';
import { cssMap, cx, jsx } from '@atlaskit/css';
import { BitbucketIcon } from '@atlaskit/logo/bitbucket/icon';
import { ConfluenceIcon } from '@atlaskit/logo/confluence/icon';
import { FocusIcon } from '@atlaskit/logo/focus/icon';
import { JiraServiceManagementIcon } from '@atlaskit/logo/jira-service-management/icon';
import { JiraIcon } from '@atlaskit/logo/jira/icon';
import { LoomIcon } from '@atlaskit/logo/loom/icon';
import { RovoIcon } from '@atlaskit/logo/rovo/icon';
import { CloseButton } from '@atlaskit/modal-dialog/close-button';
import { useModal } from '@atlaskit/modal-dialog/hooks';
import Modal from '@atlaskit/modal-dialog/modal-dialog';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import twgLogo from '../../../../assets/twg-logo.svg';
import { useAnalyticsEvents } from '../../../../common/analytics/generated/use-analytics-events';
import { TWG_WAITLIST_URL } from '../../../../constants';
import { useAtlassianUserContext } from '../../../../hooks/use-user-context';
import { messages as navMessages } from '../messages';
import { messages } from './messages';

const styles = cssMap({
	// Figma `765:29798`: borderless and 36px tall (8px around the 20px `Text`
	// line), like the feedback button it replaces. Assumes Log in is the next
	// sibling in the bar's `rightControls` (gap `space.200`, `../index.tsx`);
	// -8px brings the pair to Figma's 8px.
	signUpButton: {
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
		marginInlineEnd: token('space.negative.100'),
		borderRadius: token('radius.medium'),
		backgroundColor: token('color.background.neutral.subtle'),
		whiteSpace: 'nowrap',
		flexShrink: 0,
		transition: token('motion.button.hovered'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
			transition: token('motion.button.pressed'),
		},
	},
	// Figma `795:15137` pads the card 32px by 40px, leaving 320px of content in
	// the 400px small modal.
	card: {
		position: 'relative',
		paddingBlock: token('space.400'),
		paddingInline: token('space.500'),
		// Full-screen on phones: start below the close button and leave the
		// 288px app stack room down to a 320px viewport.
		'@media not all and (min-width: 30rem)': {
			paddingBlockStart: token('space.800'),
			paddingInline: token('space.200'),
		},
	},
	// Figma has no close button; ADS modals need one.
	closeButton: {
		position: 'absolute',
		insetBlockStart: token('space.200'),
		insetInlineEnd: token('space.200'),
	},
	// The nav bar wordmark's tokens (`BrandHomeLink`).
	wordmark: {
		font: token('font.heading.medium'),
		fontFamily: token('font.family.brand.heading'),
		fontWeight: token('font.weight.regular'),
	},
	// Figma `4351:16905`: seven 48px tiles, each overlapping the one before it by
	// 8px, make a 288px row. The row keeps that width while an app is expanded
	// (see `expandedLayout`), so this box doesn't move under the pointer.
	apps: {
		display: 'flex',
		alignSelf: 'center',
		width: '288px',
	},
	// One app: its tile, then its name while expanded. Positioned so a later app
	// paints, and takes the pointer, over an earlier one even when the earlier
	// one is rotated, which would otherwise lift it on top.
	app: {
		display: 'flex',
		position: 'relative',
		flexShrink: 0,
		alignItems: 'center',
	},
	// No pointer events, so the hover area is the unrotated app boxes, which
	// tile the row exactly; a tilted corner outside the row can't toggle it.
	tile: {
		display: 'flex',
		flexShrink: 0,
		pointerEvents: 'none',
	},
	tiltLeft: {
		transform: 'rotate(-6deg)',
	},
	tiltRight: {
		transform: 'rotate(6deg)',
	},
	// The Rovo logo has no tile of its own; Figma sits it on a white one. Fixed
	// at 48px like the other logos (not rem padding), with the 32px logo centred.
	rovoTile: {
		width: '48px',
		height: '48px',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: token('radius.large'),
		backgroundColor: token('color.rovo.background.brand.bold'),
	},
	// Clips the name while the spring opens and closes it. Open, it is the name
	// plus any trailing padding (see `expandedLayout`).
	labelClip: {
		display: 'flex',
		flexShrink: 0,
		overflow: 'hidden',
		pointerEvents: 'none',
	},
	// The name on one line at its natural width, 8px from the tile. It keeps
	// that width while clipped, so it can be measured when its app opens.
	label: {
		display: 'block',
		flexShrink: 0,
		paddingInlineStart: token('space.100'),
		whiteSpace: 'nowrap',
	},
	// Figma draws a bright line; this is the brightest neutral border token.
	rule: {
		flexGrow: 1,
		borderBlockStartWidth: token('border.width'),
		borderBlockStartStyle: 'solid',
		borderBlockStartColor: token('color.border.bold'),
	},
});

// Figma `4351:16900`, in design order. `name` is each logo's own default label,
// passed explicitly so the hover label always matches the accessible name.
const apps: ReadonlyArray<{
	name: string;
	Icon: (props: { size: 'medium' | 'xlarge'; label: string }) => JSX.Element;
	size: 'medium' | 'xlarge';
	tile?: 'tiltLeft' | 'tiltRight' | 'rovoTile';
}> = [
	{ name: 'Focus', Icon: FocusIcon, size: 'xlarge' },
	{ name: 'Confluence', Icon: ConfluenceIcon, size: 'xlarge', tile: 'tiltLeft' },
	{ name: 'Rovo', Icon: RovoIcon, size: 'medium', tile: 'rovoTile' },
	{ name: 'Jira', Icon: JiraIcon, size: 'xlarge', tile: 'tiltRight' },
	{ name: 'Jira Service Management', Icon: JiraServiceManagementIcon, size: 'xlarge' },
	{ name: 'Loom', Icon: LoomIcon, size: 'xlarge', tile: 'tiltLeft' },
	{ name: 'Bitbucket', Icon: BitbucketIcon, size: 'xlarge' },
];

// The Figma prototype's spring (about 500ms), for expanding and collapsing.
const spring: Transition = { type: 'spring', stiffness: 240, damping: 24, mass: 1 };
const instant: Transition = { duration: 0 };

// Tiles overlap the one before them by 8px at rest (Figma: 9px).
const restOverlap = 8;
const joints = apps.length - 1;
// Every tile is 48px wide (`xlarge` logos, fixed 48px Rovo tile). The overlap is
// capped so each squeezed tile keeps at least 4px visible even when enlarged
// text makes a name too long; the clip then cuts the (hidden) name instead.
const maxOverlap = 48 - 4;

/**
 * Where an expanded app's name goes. Every joint takes the same overlap `o`,
 * chosen so the row keeps its rest width: the open name clip must then be
 * `joints × (o − restOverlap)` wide. That clip is the name `w` (its 8px gap
 * included) plus trailing padding `o + 8`, so the next tile's overlap lands on
 * padding and leaves 8px clear: `o = (joints × restOverlap + w + 8) / (joints − 1)`.
 * The last app has no next tile and no padding: `o = restOverlap + w / joints`.
 * Longer names overlap more; even "Jira Service Management" (Figma: -40px)
 * leaves each squeezed tile about 5px. `maxOverlap` caps it.
 */
const expandedLayout = (index: number, w: number): { overlap: number; clipWidth: number } => {
	const overlap = Math.min(
		index === joints ? restOverlap + w / joints : (joints * restOverlap + w + 8) / (joints - 1),
		maxOverlap,
	);
	return { overlap, clipWidth: joints * (overlap - restOverlap) };
};

/**
 * The app logos. Moving over one expands it to show its name; leaving the row,
 * or moving onto no app, collapses it.
 *
 * Only real pointer movement switches apps. Expanding moves the tiles, and a
 * tile sliding under a still pointer (and the synthetic mouse events browsers
 * send for it) doesn't change the pointer's position, so it can't switch apps
 * and loop enter/leave.
 */
const AppRow = (): JSX.Element => {
	const [active, setActive] = useState<{ index: number; nameWidth: number } | null>(null);
	const appRefs = useRef<Array<HTMLElement | null>>([]);
	const nameRefs = useRef<Array<HTMLElement | null>>([]);
	// Where the last pointer event was; `null` until the pointer next enters.
	const lastPointer = useRef<{ x: number; y: number } | null>(null);
	const transition = useReducedMotion() ? instant : spring;
	const layout = active ? expandedLayout(active.index, active.nameWidth) : null;
	const overlap = layout ? layout.overlap : restOverlap;

	const handlePointerMove = (event: PointerEvent<HTMLElement>): void => {
		if (event.pointerType === 'touch') {
			return;
		}
		const { clientX: x, clientY: y } = event;
		const last = lastPointer.current;
		lastPointer.current = { x, y };
		if (last && last.x === x && last.y === y) {
			return;
		}
		const index = appRefs.current.findIndex((app) => app?.contains(event.target as Node));
		if (index === (active?.index ?? -1)) {
			return;
		}
		// Measured now, at its natural width, so late-loading fonts can't skew it.
		// `offsetWidth` ignores the modal's entrance scale; +1px covers its integer
		// rounding, as the last name has no trailing padding to absorb a lost pixel.
		const nameWidth = (nameRefs.current[index]?.offsetWidth ?? 0) + 1;
		setActive(index === -1 ? null : { index, nameWidth });
	};

	return (
		<Box
			xcss={styles.apps}
			onPointerMove={handlePointerMove}
			onPointerLeave={() => {
				lastPointer.current = null;
				setActive(null);
			}}
		>
			{apps.map(({ name, Icon, size, tile }, index) => {
				const isExpanded = index === active?.index;
				return (
					<motion.span
						key={name}
						ref={(element: HTMLElement | null) => {
							appRefs.current[index] = element;
						}}
						css={styles.app}
						initial={false}
						animate={{ marginInlineStart: index === 0 ? 0 : -overlap }}
						transition={transition}
					>
						<Box as="span" xcss={cx(styles.tile, tile && styles[tile])}>
							<Icon size={size} label={name} />
						</Box>
						{/* Hidden: the logo's own label already names it. */}
						<motion.span
							aria-hidden="true"
							css={styles.labelClip}
							initial={false}
							animate={{
								width: isExpanded && layout ? layout.clipWidth : 0,
								opacity: isExpanded ? 1 : 0,
							}}
							transition={transition}
						>
							<Box
								as="span"
								ref={(element: HTMLElement | null) => {
									nameRefs.current[index] = element;
								}}
								xcss={styles.label}
							>
								<Text size="small">{name}</Text>
							</Box>
						</motion.span>
					</motion.span>
				);
			})}
		</Box>
	);
};

// Rendered inside `Modal`, so `useModal()` resolves. Naming the dialog after
// its visible lockup (ADS `no-modal-label`) keeps the accessible name on screen.
const Lockup = (): JSX.Element => {
	const { titleId } = useModal();
	return (
		<Inline space="space.100" alignBlock="center" alignInline="center">
			{/* The nav bar's own logo (`BrandHomeLink`), so both lockups match. */}
			<img src={twgLogo} alt="" width={16} height={16} />
			<Box as="span" id={titleId} xcss={styles.wordmark}>
				<FormattedMessage {...navMessages.teamworkGraphBrand} />
			</Box>
		</Inline>
	);
};

type SignupDialogProps = {
	onClose: () => void;
};

/**
 * The sign-up dialog (Figma `795:15137`): anyone already on an Atlassian app logs
 * in, everyone else joins the waitlist. Exported so the VR story can render it open.
 */
export const SignupDialog = ({ onClose }: SignupDialogProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const { attemptUserLogin } = useAtlassianUserContext();
	const { fireEvent } = useAnalyticsEvents();

	// The nav bar's own login, attributed to this dialog like every login CTA.
	const handleLogInClick = useCallback((): void => {
		fireEvent('ui.button.clicked.twgMicrositeLogin', { location: 'signupDialog' });
		attemptUserLogin();
	}, [fireEvent, attemptUserLogin]);

	const handleJoinWaitlistClick = useCallback((): void => {
		fireEvent('ui.joinWaitlist.clicked.twgMicrositeSignupDialog', {});
	}, [fireEvent]);

	return (
		<Modal width="small" onClose={onClose} shouldScrollInViewport testId="signup-dialog">
			<Box xcss={styles.card}>
				{/* First in the DOM, as ADS asks of a custom close button. */}
				<Box xcss={styles.closeButton}>
					<CloseButton
						label={formatMessage(messages.closeLabel)}
						onClick={onClose}
						testId="signup-dialog"
					/>
				</Box>
				<Stack space="space.400">
					<Lockup />
					<Stack space="space.300">
						<AppRow />
						<Text as="p" size="large" align="center">
							<FormattedMessage {...messages.existingUsers} values={{ br: <br /> }} />
						</Text>
						<Button shouldFitContainer onClick={handleLogInClick}>
							<FormattedMessage {...messages.logInWithWorkEmail} />
						</Button>
						<Inline space="space.500" alignBlock="center">
							<Box xcss={styles.rule} />
							<Text size="small" color="color.text.subtle">
								<FormattedMessage {...messages.or} />
							</Text>
							<Box xcss={styles.rule} />
						</Inline>
						<Stack space="space.150">
							{/* LinkButton's screen-reader new-tab hint is English-only. */}
							<LinkButton
								href={TWG_WAITLIST_URL}
								target="_blank"
								rel="noopener noreferrer"
								shouldFitContainer
								onClick={handleJoinWaitlistClick}
							>
								<FormattedMessage {...messages.joinWaitlist} />
							</LinkButton>
							<Text as="p" size="small" color="color.text.subtlest" align="center">
								<FormattedMessage {...messages.waitlistHelper} />
							</Text>
						</Stack>
					</Stack>
				</Stack>
			</Box>
		</Modal>
	);
};

/**
 * The nav bar's "Sign up" button and the dialog it opens. `NavigationBar` renders
 * it for a logged-out visitor under `twg_microsite_signup_experience`, in place of
 * the feedback button and just before "Log in".
 */
export const SignupControl = (): JSX.Element => {
	const [isOpen, setIsOpen] = useState(false);
	const { fireEvent } = useAnalyticsEvents();

	const open = useCallback((): void => {
		fireEvent('ui.button.clicked.twgMicrositeSignup', {});
		setIsOpen(true);
	}, [fireEvent]);

	const close = useCallback((): void => {
		setIsOpen(false);
	}, []);

	return (
		<Fragment>
			<Pressable xcss={styles.signUpButton} onClick={open} testId="signup-control">
				<Text weight="medium">
					<FormattedMessage {...messages.signUp} />
				</Text>
			</Pressable>
			<ModalTransition>{isOpen && <SignupDialog onClose={close} />}</ModalTransition>
		</Fragment>
	);
};
